import { Text, TextButton } from '@wix/design-system';
import { useEnterpriseRefContext } from 'providers/EnterpriseContextProvider';
import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { buildEnterpriseUrlLink } from 'utils';
import { EnterpriseLanguageToFormURL } from '../../constants';
import s from './Faq.scss';

interface FaqAnswerProps {
  faqCode: string;
  isEnterpriseFaqs: boolean;
  locale: string;
  userGuid?: string;
  clientId?: string;
  isOpen: boolean;
}

export const FaqAnswer: React.FC<FaqAnswerProps> = ({
  faqCode,
  isEnterpriseFaqs,
  locale,
  userGuid,
  clientId,
  isOpen,
}) => {
  const answerKey = isEnterpriseFaqs
    ? `enterprise_plans.faq.answer.${faqCode}`
    : `${faqCode}.answer`;
  const linkKey = isEnterpriseFaqs
    ? `enterprise_plans.faq.link.${faqCode}`
    : `${faqCode}.link`;
  const { t } = useTranslation();
  const { scrollToRef } = useEnterpriseRefContext();

  const handleCTAOnClick = () => {
    const url = EnterpriseLanguageToFormURL.get(locale);

    if (url) {
      const enterpriseUrlLink = buildEnterpriseUrlLink({
        localeUrl: url,
        referrer: 'wix-pp-faq',
        locale,
        userGuid,
        clientId,
      });
      window.open(enterpriseUrlLink, '_blank');
    } else {
      return scrollToRef('faq');
    }
  };

  return (
    <Text
      weight='normal'
      size='medium'
      tagName='p'
      className={s.answer}
      dataHook={`faq-answer-${faqCode}`}
      data-testid={`faq-answer-${faqCode}`}
    >
      {t(linkKey).includes('https') ? (
        <FaqAnswerLink answerKey={answerKey} link={linkKey} isOpen={isOpen} />
      ) : (
        <FaqAnswerCallback
          answerKey={answerKey}
          link={linkKey}
          callback={() => handleCTAOnClick()}
          isOpen={isOpen}
        />
      )}
    </Text>
  );
};

const createKeyboardHandler = (
  action: () => void,
): ((event: React.KeyboardEvent) => void) => {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      action();
    }
  };
};

const FaqAnswerLink = ({ answerKey, link, isOpen }) => {
  const { t } = useTranslation();
  const handleKeyDown = createKeyboardHandler(() => {
    window.open(t(link), '_blank');
  });

  return (
    <Trans i18nKey={answerKey} value={link}>
      {answerKey}
      <TextButton
        as='a'
        size='medium'
        target='_blank'
        href={t(link)}
        onKeyDown={handleKeyDown}
        tabIndex={isOpen ? 0 : -1}
      >
        {link}
      </TextButton>
    </Trans>
  );
};

const FaqAnswerCallback = ({ answerKey, link, callback, isOpen }) => {
  const handleKeyDown = createKeyboardHandler(callback);

  return (
    <Trans i18nKey={answerKey} value={link}>
      {answerKey}
      <TextButton
        as='span'
        size='medium'
        onClick={callback}
        onKeyDown={handleKeyDown}
        tabIndex={isOpen ? 0 : -1}
      >
        {link}
      </TextButton>
    </Trans>
  );
};
